import React, { useMemo } from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import FirmwareComplianceChart from 'components/FirmwareComplianceChart';
import CircularLoader from 'components/CircularLoader';

import icn_pie_chart from 'assets/images/icons/icn_pie_chart.png';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 550,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — Latest Version Compliance &gt; 90%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Nearly all devices are running the latest firmware. Security patch compliance is high.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — 60% ≤ Latest Version Compliance ≤ 90%</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Many devices are running outdated firmware, posing a moderate security and consistency risk.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Schedule a maintenance window to upgrade devices on Previous or Legacy versions.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — Latest Version Compliance &lt; 60% / Update Failures Detected</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        Obsolete firmware or repeated update failures pose a high security risk.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Prioritize manual updates, starting with edge-facing gateways.</li>
        <li>Investigate update failures and plan replacement of End-of-Life devices.</li>
      </Box>
    </Box>
  </Box>
);

const FirmwareUpdateStatus = ({ data, loading, isEmpty, onClick }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const { latestCount, previousCount, legacyCount } = data || { latestCount: 0, previousCount: 0, legacyCount: 0 };
  const total = latestCount + previousCount + legacyCount;

  const getStatus = (latest, all) => {
    if (all === 0) return { label: 'Good', color: '#4CAF50' };
    const ratio = latest / all;
    if (ratio >= 0.9) return { label: 'Good', color: '#4CAF50' };
    if (ratio >= 0.6) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const status = data ? getStatus(latestCount, total) : { label: 'Good', color: '#4CAF50' };

  const percentages = useMemo(() => {
    if (total === 0) return { latest: 0, previous: 0, legacy: 0 };
    return {
      latest: Math.round((latestCount / total) * 100),
      previous: Math.round((previousCount / total) * 100),
      legacy: Math.round((legacyCount / total) * 100)
    };
  }, [total, latestCount, previousCount, legacyCount]);

  const chartData = useMemo(() => [
    { value: latestCount, name: 'Latest Version', itemStyle: { color: '#172664' } },
    { value: previousCount, name: 'Previous Version', itemStyle: { color: '#87A2E2' } },
    { value: legacyCount, name: 'Legacy', itemStyle: { color: '#008000' } }
  ], [latestCount, previousCount, legacyCount]);

  const legendFormatter = (name) => {
    const count = name === 'Latest Version' ? latestCount : (name === 'Previous Version' ? previousCount : legacyCount);
    const val = name === 'Latest Version' ? percentages.latest : (name === 'Previous Version' ? percentages.previous : percentages.legacy);
    return `{name|${name}} {percent|${count} (${val}%)}`;
  };

  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_pie_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Firmware Update Status
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: '25%' }, pr: { xs: 0, md: 4 }, mb: { xs: 3, md: 0 } }}>
            <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Security Patch Compliance</Typography>
            <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2, mb: 2 }}>
              {status.label}
            </Typography>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mr: 4, borderColor: '#EDF2F7' }} />

          <Box sx={{ flex: 1, width: '100%' }}>
            <FirmwareComplianceChart
              data={chartData}
              totalValue={total}
              legendFormatter={legendFormatter}
              height="250px"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default FirmwareUpdateStatus;